// Le pont entre l'application actuelle et React.
//
// La migration se fait écran par écran, jamais d'un bloc. Pour cela il faut que
// les deux mondes cohabitent : le routeur de `app.js` appelle `rendre(root, ctx)`
// sur chaque vue, et attend en retour une fonction de nettoyage. Ce fichier
// fabrique une vue de cette forme à partir d'un composant React.
//
// Un écran migré ressemble alors à :
//
//     import { vueReact } from "../react/pont.jsx";
//     import Sponsoring from "../react/ecrans/Sponsoring.jsx";
//     export const rendre = vueReact(Sponsoring);
//
// et `app.js` n'a pas à savoir qu'il vient de changer de monde.

import { createRoot } from "react-dom/client";
import { createElement, StrictMode } from "react";

/**
 * Transforme un composant React en vue au format attendu par le routeur.
 *
 * @param {Function} Composant  reçoit { ctx, params } en props
 * @returns {(root: HTMLElement, ctx: object, params?: object) => Function}
 *          la fonction `rendre` que le routeur appelle, rendant le nettoyage
 */
export function vueReact(Composant) {
  return function rendre(root, ctx, params = {}) {
    // Le routeur RÉUTILISE le même conteneur d'un écran à l'autre, et les vues
    // d'avant le vident en écrivant leur HTML dedans. On fait pareil, sinon
    // l'ancien écran resterait affiché sous le nouveau.
    root.innerHTML = "";

    // ⚠️ On rend dans un enfant dédié, JAMAIS dans `root` lui-même.
    //
    // React vide son conteneur au démontage. Or le démontage doit être différé
    // (React interdit de démonter pendant qu'il rend, et une navigation partie
    // d'un composant tomberait en plein rendu). Si ce conteneur était `root`,
    // le démontage arriverait APRÈS que le routeur y ait dessiné l'écran
    // suivant — et l'effacerait. C'était l'écran noir qu'il fallait recharger.
    const hote = document.createElement("div");
    hote.className = "hote-react";
    root.appendChild(hote);

    const racine = createRoot(hote);
    racine.render(createElement(StrictMode, null, createElement(Composant, { ctx, params })));

    return () => {
      // Détaché tout de suite : l'écran se vide sans attendre.
      hote.remove();
      // Puis on rend la mémoire à React, sur un conteneur désormais isolé —
      // il ne peut plus rien effacer d'autre que lui-même.
      setTimeout(() => racine.unmount(), 0);
    };
  };
}
